import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import { buildFormLayoutStyles } from "../layout/formLayout";

export interface OpenSearchDiscoverViewerComponentStyles {
  detailRoot: AppSx;
  detailHeader: AppSx;
  detailTimestamp: AppSx;
  detailEventLabel: AppSx;
  viewSurroundingButton: AppSx;
  detailTabs: AppSx;
  detailTableContainer: AppSx;
  detailTable: AppSx;
  detailFieldHeader: AppSx;
  detailValueHeader: AppSx;
  detailFieldCell: AppSx;
  detailValueCell: AppSx;
  detailJsonContainer: AppSx;
  loadingStack: AppSx;
  placeholderStack: AppSx;
  emptyText: AppSx;
  listContainer: (borderColor?: string, listBackgroundColor?: string) => AppSx;
  listRow: (selected: boolean, selectedRowColor?: string) => AppSx;
  listRowButton: (selected: boolean) => AppSx;
  listRowIcon: AppSx;
  listRowContent: AppSx;
  listRowTimestamp: AppSx;
  listRowPreview: AppSx;
}

export function buildOpenSearchDiscoverViewerComponentStyles(
  _theme: Theme
): OpenSearchDiscoverViewerComponentStyles {
  return {
    detailRoot: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    detailHeader: {
      px: 2,
      py: 1.25,
      borderBottom: "1px solid",
      borderColor: "divider",
      flexShrink: 0,
    },
    detailTimestamp: {
      fontFamily: "monospace",
      fontSize: "0.8125rem",
      flex: 1,
      minWidth: 0,
      pr: 1,
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    detailEventLabel: { fontWeight: 700 },
    viewSurroundingButton: { whiteSpace: "nowrap" },
    detailTabs: {
      mx: 1,
      mt: 1,
      mb: 0.5,
      minHeight: 40,
      flexShrink: 0,
      width: "fit-content",
      maxWidth: "calc(100% - 16px)",
    },
    detailTableContainer: { flex: 1, minHeight: 0, overflow: "auto", width: "100%" },
    detailTable: { tableLayout: "fixed", width: "100%" },
    detailFieldHeader: { width: 140, maxWidth: 140, fontWeight: 700, bgcolor: "background.paper" },
    detailValueHeader: { fontWeight: 700, bgcolor: "background.paper" },
    detailFieldCell: {
      verticalAlign: "top",
      fontFamily: "monospace",
      fontSize: "0.8125rem",
      color: "text.secondary",
      width: 140,
      maxWidth: 140,
      wordBreak: "break-word",
    },
    detailValueCell: {
      verticalAlign: "top",
      fontFamily: "monospace",
      fontSize: "0.8125rem",
      wordBreak: "break-all",
      overflowWrap: "anywhere",
      whiteSpace: "pre-wrap",
      lineHeight: 1.45,
    },
    detailJsonContainer: { flex: 1, minHeight: 0, overflow: "hidden", p: 1.5, width: "100%" },
    loadingStack: { py: 6 },
    placeholderStack: { py: 5 },
    emptyText: { py: 5, textAlign: "center" },
    listContainer: (borderColor, listBackgroundColor) => ({
      minHeight: 480,
      maxHeight: 640,
      border: "1px solid",
      borderColor: borderColor ?? "divider",
      borderRadius: 2,
      overflow: "auto",
      bgcolor: listBackgroundColor ?? "background.paper",
    }),
    listRow: (selected, selectedRowColor) => ({
      display: "flex",
      alignItems: "flex-start",
      gap: 0.5,
      px: 0.75,
      py: 0.75,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: selected ? selectedRowColor ?? "action.selected" : "transparent",
      "&:hover": {
        bgcolor: selected ? selectedRowColor ?? "action.selected" : "action.hover",
      },
    }),
    listRowButton: (selected) => ({
      mt: 0.125,
      color: selected ? "primary.main" : "text.secondary",
    }),
    listRowIcon: { fontSize: 18 },
    listRowContent: {
      flex: 1,
      minWidth: 0,
      cursor: "pointer",
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 1.25 },
      alignItems: { sm: "flex-start" },
    },
    listRowTimestamp: {
      flexShrink: 0,
      fontFamily: "monospace",
      fontSize: "0.75rem",
      color: "text.secondary",
      whiteSpace: "nowrap",
      minWidth: { sm: 190 },
    },
    listRowPreview: {
      flex: 1,
      fontFamily: "monospace",
      fontSize: "0.75rem",
      color: "text.primary",
      wordBreak: "break-word",
      lineHeight: 1.45,
    },
  };
}

export interface OpenSearchEventLogPanelComponentStyles {
  loadingStack: AppSx;
  placeholderStack: AppSx;
  emptyText: AppSx;
  eventPaper: (borderColor?: string, backgroundColor?: string) => AppSx;
  summaryBox: AppSx;
  timestamp: AppSx;
  title: AppSx;
  subtitle: AppSx;
  subtitleText: AppSx;
  fieldGrid: AppSx;
  fieldBox: AppSx;
  fieldValue: (monospace?: boolean) => AppSx;
  accordion: AppSx;
  accordionDetails: AppSx;
}

export function buildOpenSearchEventLogPanelComponentStyles(
  theme: Theme
): OpenSearchEventLogPanelComponentStyles {
  const form = buildFormLayoutStyles(theme);
  return {
    loadingStack: { py: 6 },
    placeholderStack: { py: 5 },
    emptyText: { py: 5, textAlign: "center" },
    eventPaper: (borderColor, backgroundColor) => ({
      ...form.pageSectionPanel,
      mb: 0,
      p: 1.5,
      borderColor: borderColor ?? "divider",
      bgcolor: backgroundColor ?? "background.paper",
    }),
    summaryBox: { minWidth: 0 },
    timestamp: { display: "block", mb: 0.25 },
    title: { fontWeight: 700, wordBreak: "break-word" },
    subtitle: { display: "block", mt: 0.25 },
    subtitleText: { mt: 0.25, wordBreak: "break-word" },
    fieldGrid: {
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
      gap: 1,
    },
    fieldBox: {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      px: 1.25,
      py: 1,
      minWidth: 0,
    },
    fieldValue: (monospace) => ({
      wordBreak: "break-word",
      fontFamily: monospace ? "monospace" : undefined,
      fontSize: monospace ? "0.8125rem" : undefined,
    }),
    accordion: {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "12px !important",
      "&:before": { display: "none" },
    },
    accordionDetails: { pt: 0 },
  };
}

export interface OpenSearchIndexPickerComponentStyles {
  popper: AppSx;
  paper: AppSx;
  checkbox: AppSx;
}

export function buildOpenSearchIndexPickerComponentStyles(theme: Theme): OpenSearchIndexPickerComponentStyles {
  return {
    popper: { zIndex: theme.zIndex.modal + 1 },
    paper: { maxHeight: 280 },
    checkbox: { mr: 1 },
  };
}

export interface ChatIndexPickerComponentStyles {
  triggerButton: (iconColor?: string) => AppSx;
  popoverPaper: (shell: {
    surface: string;
    assistantText: string;
    border: string;
    muted: string;
  }, darkMode: boolean) => AppSx;
  title: (assistantText: string) => AppSx;
  hint: (muted: string) => AppSx;
  chipsRow: AppSx;
  actions: AppSx;
}

export function buildChatIndexPickerComponentStyles(_theme: Theme): ChatIndexPickerComponentStyles {
  return {
    triggerButton: (iconColor) => ({
      color: iconColor,
      width: 36,
      height: 36,
      flexShrink: 0,
    }),
    popoverPaper: (shell, darkMode) => ({
      p: 2,
      width: 360,
      maxWidth: "92vw",
      bgcolor: shell.surface,
      color: shell.assistantText,
      border: `1px solid ${shell.border}`,
      boxShadow: darkMode
        ? "0 8px 32px rgba(0, 0, 0, 0.55)"
        : "0 8px 24px rgba(60, 64, 67, 0.18)",
      "& .MuiAutocomplete-listbox": {
        bgcolor: shell.surface,
        color: shell.assistantText,
      },
      "& .MuiAutocomplete-noOptions": {
        color: shell.muted,
      },
    }),
    title: (assistantText) => ({
      mb: 0.5,
      fontWeight: 600,
      color: assistantText,
    }),
    hint: (muted) => ({
      display: "block",
      mb: 1.5,
      color: muted,
    }),
    chipsRow: { mb: 1.5 },
    actions: { mt: 2 },
  };
}
